import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminQuickNav from '../../components/AdminQuickNav';
import { rbacService, ADMIN_EMAIL } from '../../services/rbacService';
import type {
  UserRole,
  RolePermissions,
  RoleAssignment,
} from '../../types/rbac';
import {
  DEFAULT_ROLE_PERMISSIONS,
  ROLE_LABELS,
  PERMISSION_LABELS,
} from '../../types/rbac';
import {
  HiShieldCheck,
  HiUserAdd,
  HiTrash,
  HiPencilAlt,
  HiSearch,
  HiCode,
  HiCheck,
  HiX,
  HiUsers,
  HiSparkles,
} from 'react-icons/hi';

export default function RolesAdminPage() {
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [registeredUsers, setRegisteredUsers] = useState<
    { id: string; email: string; display_name: string | null }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [emailInput, setEmailInput] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole>('editor');
  const [customPerms, setCustomPerms] = useState<RolePermissions>({
    ...DEFAULT_ROLE_PERMISSIONS.editor,
  });
  const [showAdvancedPerms, setShowAdvancedPerms] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showSqlModal, setShowSqlModal] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assigned, users] = await Promise.all([
        rbacService.getAllAssignments(),
        rbacService.getRegisteredUsers(),
      ]);
      setAssignments(assigned);
      setRegisteredUsers(users);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'خطا در بارگذاری اطلاعات نقش‌ها.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const unsub = rbacService.subscribe(() => {
      rbacService.getAllAssignments().then(setAssignments);
    });
    return unsub;
  }, []);

  const handleRoleChange = (role: UserRole) => {
    setSelectedRole(role);
    setCustomPerms({ ...DEFAULT_ROLE_PERMISSIONS[role] });
  };

  const handlePermToggle = (key: keyof RolePermissions) => {
    setCustomPerms((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setMsg({ type: 'error', text: 'لطفاً یک ایمیل معتبر وارد کنید.' });
      return;
    }

    setSaving(true);
    setMsg(null);
    try {
      await rbacService.assignRole(email, selectedRole, customPerms);
      setMsg({
        type: 'success',
        text: `نقش «${ROLE_LABELS[selectedRole].title}» با موفقیت به ${email} اختصاص یافت.`,
      });
      setEmailInput('');
      setSelectedRole('editor');
      setCustomPerms({ ...DEFAULT_ROLE_PERMISSIONS.editor });
      setIsEditing(null);
      setShowAdvancedPerms(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'خطا در تخصیص نقش.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (assignment: RoleAssignment) => {
    setIsEditing(assignment.email);
    setEmailInput(assignment.email);
    setSelectedRole(assignment.role);
    setCustomPerms({
      ...DEFAULT_ROLE_PERMISSIONS[assignment.role],
      ...(assignment.custom_permissions || {}),
    });
    setShowAdvancedPerms(true);
    window.scrollTo({ top: 350, behavior: 'smooth' });
  };

  const handleRevoke = async (email: string) => {
    if (email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
      alert('امکان حذف دسترسی مدیر ارشد سیستم وجود ندارد.');
      return;
    }
    if (!confirm(`آیا از لغو نقش و دسترسی‌های کاربر ${email} اطمینان دارید؟`)) return;

    try {
      await rbacService.revokeRole(email);
      setMsg({ type: 'success', text: `دسترسی‌های کاربر ${email} با موفقیت لغو شد.` });
      await loadData();
    } catch (err: any) {
      alert(err.message || 'خطا در لغو نقش.');
    }
  };

  const filteredAssignments = assignments.filter((a) =>
    a.email.toLowerCase().includes(search.toLowerCase())
  );

  const adminCount = assignments.filter((a) => a.role === 'admin').length;
  const editorCount = assignments.filter((a) => a.role === 'editor').length;
  const modCount = assignments.filter((a) => a.role === 'moderator').length;

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16 px-4 md:px-8" dir="rtl">
      <Helmet>
        <title>مدیریت نقش‌ها و دسترسی‌ها (RBAC) | پنل مدیریت Active Legend</title>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <AdminQuickNav />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/20 rounded-2xl text-primary border border-primary/30">
              <HiShieldCheck className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white">
                مدیریت نقش‌ها و سطوح دسترسی (RBAC by Email)
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                تعریف و مدیریت دقیق مجوزهای پنل ادمین بر اساس ایمیل کاربران
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowSqlModal(!showSqlModal)}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-xs text-gray-300 px-4 py-2.5 rounded-xl border border-white/10 transition-all self-start md:self-auto"
          >
            <HiCode className="w-4 h-4 text-primary" />
            <span>اسکریپت دیتابیس Supabase</span>
          </button>
        </div>

        {/* Status Message */}
        {msg && (
          <div
            className={`p-4 rounded-2xl mb-6 text-sm font-medium border flex items-center justify-between ${
              msg.type === 'success'
                ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300'
                : 'bg-red-950/60 border-red-500/40 text-red-300'
            }`}
          >
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} className="text-white/60 hover:text-white">
              <HiX className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-zinc-900/80 border border-amber-500/30 rounded-2xl p-5 shadow-lg">
            <div className="text-xs text-amber-400 font-bold mb-1">مدیران کل (Super Admins)</div>
            <div className="text-3xl font-black text-white">{adminCount}</div>
            <div className="text-xs text-gray-400 mt-2">دسترسی تام به کل پلتفرم</div>
          </div>

          <div className="bg-zinc-900/80 border border-blue-500/30 rounded-2xl p-5 shadow-lg">
            <div className="text-xs text-blue-400 font-bold mb-1">سردبیران و نویسندگان (Editors)</div>
            <div className="text-3xl font-black text-white">{editorCount}</div>
            <div className="text-xs text-gray-400 mt-2">دسترسی به بلاگ، هوش مصنوعی و لینک‌ها</div>
          </div>

          <div className="bg-zinc-900/80 border border-emerald-500/30 rounded-2xl p-5 shadow-lg">
            <div className="text-xs text-emerald-400 font-bold mb-1">ناظران انجمن (Moderators)</div>
            <div className="text-3xl font-black text-white">{modCount}</div>
            <div className="text-xs text-gray-400 mt-2">نظارت بر دیدگاه‌ها و کامنت‌ها</div>
          </div>
        </div>

        {/* SQL Script Collapsible Modal */}
        {showSqlModal && (
          <div className="bg-zinc-900 border border-primary/40 rounded-3xl p-6 mb-8 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <HiCode className="w-5 h-5" />
                کد SQL جهت همگام‌سازی کامل جداول در کنسول Supabase (اختیاری)
              </div>
              <button
                onClick={() => setShowSqlModal(false)}
                className="text-gray-400 hover:text-white text-xs px-2 py-1 bg-white/5 rounded-lg"
              >
                بستن
              </button>
            </div>
            <p className="text-xs text-gray-300 mb-3 leading-relaxed">
              سیستم به صورت خودکار با لایه مقاومت هوشمند، نقش‌ها را ذخیره و مدیریت می‌کند. در صورت تمایل می‌توانید کد زیر را در بخش SQL Editor داشبورد Supabase اجرا نمایید تا جدول نقش‌ها بدون خطای بازگشتی (Recursion) بروز شود:
            </p>
            <pre className="bg-black/80 border border-white/10 rounded-2xl p-4 text-xs font-mono text-emerald-400 overflow-x-auto text-left" dir="ltr">
{`-- ایجاد یا تنظیم مجدد جدول نقش‌ها با سیاست‌های امن بدون بازگشت
CREATE TABLE IF NOT EXISTS public.user_roles (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id uuid REFERENCES auth.users(id) ON DELETE CASCADE,
  role text NOT NULL DEFAULT 'editor',
  created_at timestamptz DEFAULT now()
);

-- غیرفعال‌سازی پالیسی‌های تکرارشونده و بازنویسی با پالیسی امن
ALTER TABLE public.user_roles ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Public read user_roles" ON public.user_roles;
CREATE POLICY "Public read user_roles" ON public.user_roles FOR SELECT USING (true);
`}
            </pre>
          </div>
        )}

        {/* Assignment Form Card */}
        <div className="bg-zinc-900/90 border border-white/10 rounded-3xl p-6 md:p-8 mb-10 shadow-2xl">
          <div className="flex items-center gap-2 text-primary font-bold text-sm mb-4">
            <HiUserAdd className="w-5 h-5" />
            {isEditing ? `ویرایش نقش کاربر: ${isEditing}` : 'اختصاص نقش و دسترسی جدید بر اساس ایمیل'}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email Input */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">
                  آدرس ایمیل کاربر <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="مثال: editor@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  disabled={Boolean(isEditing)}
                  className="w-full bg-black/60 border border-white/15 focus:border-primary rounded-2xl px-4 py-3 text-sm text-white focus:outline-none transition-all placeholder:text-gray-600 disabled:opacity-50"
                  dir="ltr"
                />
              </div>

              {/* Role Dropdown */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">
                  انتخاب نقش اصلی <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => handleRoleChange(e.target.value as UserRole)}
                  className="w-full bg-black/60 border border-white/15 focus:border-primary rounded-2xl px-4 py-3 text-sm text-white focus:outline-none transition-all"
                >
                  <option value="admin">مدیر کل (Super Admin) — دسترسی نامحدود</option>
                  <option value="editor">سردبیر / نویسنده (Editor) — وبلاگ، هوش مصنوعی، دانلود</option>
                  <option value="moderator">ناظر انجمن (Moderator) — تایید و مدیریت کامنت‌ها</option>
                </select>
              </div>
            </div>

            {/* Role Description Card */}
            <div className="p-4 rounded-2xl bg-black/40 border border-white/10 text-xs text-gray-300 flex items-start gap-3">
              <HiSparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-white">{ROLE_LABELS[selectedRole].title}: </span>
                {ROLE_LABELS[selectedRole].desc}
              </div>
            </div>

            {/* Granular Capabilities Accordion */}
            <div className="border border-white/10 rounded-2xl p-4 bg-black/20">
              <button
                type="button"
                onClick={() => setShowAdvancedPerms(!showAdvancedPerms)}
                className="w-full flex items-center justify-between text-xs font-bold text-gray-300 hover:text-white"
              >
                <span>تنظیمات پیشرفته دسترسی‌های جزئی (Granular Capabilities)</span>
                <span className="text-primary text-xs underline">
                  {showAdvancedPerms ? 'بستن تنظیمات جزئی' : 'مشاهده و سفارشی‌سازی مجوزها'}
                </span>
              </button>

              {showAdvancedPerms && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
                  {(Object.keys(PERMISSION_LABELS) as (keyof RolePermissions)[]).map((key) => {
                    const isChecked = customPerms[key];
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-3 p-3 rounded-xl border text-xs cursor-pointer transition-all ${
                          isChecked
                            ? 'bg-primary/10 border-primary/40 text-white'
                            : 'bg-black/30 border-white/5 text-gray-400 hover:border-white/20'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handlePermToggle(key)}
                          className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-600 bg-black/40"
                        />
                        <span className="font-medium">{PERMISSION_LABELS[key]}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Submit & Cancel Buttons */}
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-black font-extrabold px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all text-sm disabled:opacity-50"
              >
                {saving ? (
                  <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiCheck className="w-5 h-5" />
                )}
                <span>{isEditing ? 'ذخیره تغییرات دسترسی' : 'تخصیص و فعال‌سازی نقش'}</span>
              </button>

              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(null);
                    setEmailInput('');
                    setSelectedRole('editor');
                    setShowAdvancedPerms(false);
                  }}
                  className="bg-white/10 hover:bg-white/20 text-white font-medium px-4 py-3 rounded-2xl text-sm transition-all"
                >
                  انصراف
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Existing Assignments Section */}
        <div className="bg-zinc-900/90 border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <HiUsers className="w-6 h-6 text-primary" />
                لیست کاربران دارای نقش‌های مدیریتی ({filteredAssignments.length})
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                کاربران ثبت‌شده در این فهرست به بخش‌های مدیریتی مشخص‌شده دسترسی دارند
              </p>
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder="جستجوی ایمیل..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-black/60 border border-white/15 focus:border-primary rounded-2xl pr-10 pl-4 py-2 text-xs text-white focus:outline-none w-full sm:w-64"
                dir="ltr"
              />
              <HiSearch className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">در حال بارگذاری نقش‌ها...</div>
          ) : filteredAssignments.length === 0 ? (
            <div className="text-center py-12 text-gray-400 bg-black/30 rounded-2xl border border-white/5">
              کاربری با این مشخصات یافت نشد.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAssignments.map((a) => {
                const isMasterAdmin = a.email.toLowerCase() === ADMIN_EMAIL.toLowerCase();
                const roleMeta = ROLE_LABELS[a.role];

                return (
                  <div
                    key={a.email}
                    className="bg-black/50 border border-white/10 hover:border-white/20 rounded-2xl p-5 transition-all flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs font-black px-3 py-1 rounded-full bg-gradient-to-r ${roleMeta.color}`}
                          >
                            {roleMeta.title.split(' ')[0]}
                          </span>
                          {isMasterAdmin && (
                            <span className="text-[10px] bg-red-950 text-red-300 border border-red-500/40 px-2 py-0.5 rounded-full font-bold">
                              مالک پلتفرم
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(a)}
                            title="ویرایش دسترسی‌ها"
                            className="p-2 text-gray-400 hover:text-primary hover:bg-white/5 rounded-xl transition-all"
                          >
                            <HiPencilAlt className="w-4 h-4" />
                          </button>
                          {!isMasterAdmin && (
                            <button
                              onClick={() => handleRevoke(a.email)}
                              title="لغو نقش"
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-white/5 rounded-xl transition-all"
                            >
                              <HiTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="text-sm font-mono text-white font-bold mb-2 break-all" dir="ltr">
                        {a.email}
                      </div>

                      <div className="text-xs text-gray-400 mb-3">{roleMeta.desc}</div>
                    </div>

                    {/* Permissions summary */}
                    <div className="pt-3 border-t border-white/10 flex flex-wrap gap-1.5">
                      {a.role === 'admin' ? (
                        <span className="text-[11px] bg-amber-500/10 text-amber-300 border border-amber-500/20 px-2 py-0.5 rounded-lg">
                          تمامی مجوزها فعال است
                        </span>
                      ) : (
                        (Object.keys(PERMISSION_LABELS) as (keyof RolePermissions)[])
                          .filter((p) => {
                            const perms = a.custom_permissions || DEFAULT_ROLE_PERMISSIONS[a.role];
                            return perms[p];
                          })
                          .slice(0, 4)
                          .map((p) => (
                            <span
                              key={p}
                              className="text-[10px] bg-white/5 text-gray-300 border border-white/10 px-2 py-0.5 rounded-lg"
                            >
                              {PERMISSION_LABELS[p]}
                            </span>
                          ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Registered Users Quick-Add Section */}
          {registeredUsers.length > 0 && (
            <div className="mt-8 pt-8 border-t border-white/10">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <HiUsers className="w-4 h-4 text-primary" />
                انتخاب سریع از میان کاربران ثبت‌نام‌شده ({registeredUsers.length} کاربر):
              </h3>
              <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto p-2 bg-black/40 rounded-2xl border border-white/5">
                {registeredUsers.map((u) => {
                  const alreadyAssigned = assignments.some(
                    (a) => a.email.toLowerCase() === u.email.toLowerCase()
                  );
                  return (
                    <button
                      key={u.id}
                      onClick={() => {
                        setEmailInput(u.email);
                        window.scrollTo({ top: 350, behavior: 'smooth' });
                      }}
                      className={`text-xs px-3 py-1.5 rounded-xl border transition-all flex items-center gap-2 ${
                        alreadyAssigned
                          ? 'bg-zinc-800/80 border-white/10 text-gray-400'
                          : 'bg-zinc-900 border-white/10 hover:border-primary text-gray-200 hover:text-white'
                      }`}
                    >
                      <span className="font-mono" dir="ltr">{u.email}</span>
                      {u.display_name && (
                        <span className="text-[10px] text-gray-500">({u.display_name})</span>
                      )}
                      {alreadyAssigned && (
                        <span className="text-[10px] text-primary font-bold">✓ دارای نقش</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
